import { MergeQueue } from 'src/components/pipelines/models/MergeQueue';
import { MergeStrategy } from 'src/types/pull-request';
import { PullRequest } from 'src/types/pull-requests-table';

export type MergeQueues = MergeQueue[];

export enum MergeQueueState {
  ACTIVE = 'ACTIVE',
  DRAINING = 'DRAINING',
  INACTIVE = 'INACTIVE',
}

export enum MergeQueueStrategy {
  OPTIMISTIC_BATCHED_PR = 'OPTIMISTIC_BATCHED_PR',
  OPTIMISTIC_PER_PR = 'OPTIMISTIC_PER_PR',
  STRICT_BATCHED_PR = 'STRICT_BATCHED_PR',
  STRICT_PER_PR = 'STRICT_PER_PR',
}

export enum MergeQueueBranchMode {
  BRANCH_NAME = 'BRANCH_NAME',
  BRANCH_TYPE = 'BRANCH_TYPE',
  BRANCH_PATTERN = 'BRANCH_PATTERN',
}

export enum PullRequestsOrderingCriteria {
  TIME_BASED = 'TIME_BASED',
  BRANCH_PATTERN_AND_TIME_BASED = 'BRANCH_PATTERN_AND_TIME_BASED',
}

export type MergeQueueBranch = {
  mode: MergeQueueBranchMode;
  name?: string;
  type?: string;
  pattern?: string;
};

export type PullRequestsOrdering = {
  criteria: PullRequestsOrderingCriteria;
  source_branch_patterns: string[];
};

export type MergeQueueMergeStrategy =
  | MergeStrategy.MergeCommit
  | MergeStrategy.Squash
  | MergeStrategy.RebaseMerge;

export type MergeQueuePayload = {
  name: string;
  strategy: MergeQueueStrategy;
  branch: MergeQueueBranch;
  build_concurrency: number;
  state?: MergeQueueState;
  merge_strategy?: MergeQueueMergeStrategy;
  maximum_batch_wait_time_in_seconds?: number;
  merge_batch_size?: number;
  pullrequests_ordering?: PullRequestsOrdering;
};

export type MergeQueueEditPayload = {
  build_concurrency?: number;
  state?: MergeQueueState;
  merge_strategy?: MergeQueueMergeStrategy;
  maximum_batch_wait_time_in_seconds?: number;
  merge_batch_size?: number;
  pullrequests_ordering?: PullRequestsOrdering;
};

export type MergeQueueResponse = MergeQueuePayload & {
  type: 'merge-queue';
  uuid: string;
  created_on: string;
  last_updated_on: string;
  build_concurrency: number;
  pullrequests_count: number;
};

export type UseMergeQueueActionParams = {
  mergeQueueUuid?: string;
  owner: string;
  slug: string;
  onClose: () => void;
  /** Create: called with API response. Edit: called with no argument. */
  onSuccess?: (created?: MergeQueueResponse) => void;
};

export type MergeQueuesResourceResponse = {
  page: number;
  pagelen: number;
  size: number;
  values: MergeQueues;
};

export type MergeQueuesProps = {
  mergeQueues: MergeQueuesResourceResponse | null;
  mergeQueuesLoading: boolean;
};

export type MergeQueuesInlineSettingsProps = {
  mergeQueue: MergeQueue;
  pullRequestsCount: number;
  onClearClick?: () => void;
  onDeleteClick?: () => void;
  onEditClick?: () => void;
  onStateUpdate?: (state: MergeQueueState) => void;
  onViewClick?: () => void;
};

export type MergeQueuePullRequestActionsProps = {
  onDequeueClick?: () => void;
  onMoveToTopClick?: () => void;
};

export enum MergeQueueActionModalType {
  ACTIVATE = 'ACTIVATE',
  DELETE = 'DELETE',
  CLEAR = 'CLEAR',
  DEQUEUE_PR = 'DEQUEUE_PR',
  MOVE_TO_TOP_PR = 'MOVE_TO_TOP_PR',
}

export type MergeQueuesState = {
  isLoading: boolean;
  isCreateModalOpen: boolean;
  isEditModalOpen: boolean;
  isCreateEditModalLoading: boolean;
  isMergeQueuePullRequestsModalOpen: boolean;
  isEmpty: boolean;
  focusedMergeQueue?: MergeQueue;
  focusedPullRequest?: number;
  mergeQueues: MergeQueues;
  mergeQueuesPages: number[];
  mergeQueueActionModal: MergeQueueActionModalType | null;
};

export enum WaitTimeUnit {
  SECONDS = 'SECONDS',
  MINUTES = 'MINUTES',
  HOURS = 'HOURS',
}

export type MergeQueueFormState = {
  name: string;
  strategy: MergeQueueStrategy;
  targetBranchName: string;
  buildConcurrency: number;
  mergeStrategy?: MergeQueueMergeStrategy;
  maximumBatchWaitTime?: number;
  maximumBatchWaitTimeUnit?: WaitTimeUnit;
  mergeBatchSize?: number;
  pullRequestsOrdering?: {
    criteria: PullRequestsOrderingCriteria;
    sourceBranchPatterns: string[];
  };
};

export type MergeQueueEditFormState = {
  state?: MergeQueueState;
  buildConcurrency?: number;
  mergeStrategy?: MergeQueueMergeStrategy;
  maximumBatchWaitTime?: number;
  maximumBatchWaitTimeUnit?: WaitTimeUnit;
  mergeBatchSize?: number;
  pullRequestsOrdering?: {
    criteria: PullRequestsOrderingCriteria;
    sourceBranchPatterns: string[];
  };
  mergeQueueUuid?: string;
};

export type BranchesResponse = {
  values: Array<{
    name: string;
    target: { hash: string };
  }>;
};

export type MergeQueuePullRequestStatus =
  | 'QUEUED'
  | 'PROCESSING'
  | 'MERGING'
  | 'DEQUEUING'
  | 'MERGED'
  | 'DEQUEUED'
  | 'REMOVED';

export type MergeQueuePullRequest = {
  build_number: number;
  merge_queue_uuid: string;
  pr_id: string;
  pr_status: MergeQueuePullRequestStatus;
  pr_order: number;
  pullrequest: PullRequest;
  target_branch: string;
  created_on: string;
  last_updated_on: string;
};

export type MergeQueuePullRequestsParams = {
  mergeQueueUuid: string;
  owner: string;
  slug: string;
  page?: string;
};

export type MergeQueuePullRequestsResponse = {
  page: number;
  pagelen: number;
  size: number;
  values: MergeQueuePullRequest[];
};

export type UseQueuedPullRequestsParams = {
  mergeQueueUuid: string;
  page: string;
  owner: string;
  slug: string;
};

export type UseQueuedPullRequestsResult = {
  isQueuedPullRequestsLoading: boolean;
  mergeQueuePullRequests: MergeQueuePullRequest[];
  queuedPullRequestsPages: number[];
};

export type UsePullRequestActionParams = {
  mergeQueueUuid: string;
  pullRequestId: number;
  owner: string;
  slug: string;
  onClose: () => void;
};
